import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEOHead — one component that owns the per-page head fragment.
 *
 * Collapses the per-page Helmet boilerplate that's scattered across 80+
 * page components today. New pages should import this instead of hand-rolling
 * Helmet blocks. Existing pages will be migrated incrementally.
 *
 * Contract:
 *  - `title` is injected verbatim.
 *  - `description` is injected verbatim.
 *  - `canonical` is required and self-referential unless the page is a
 *    near-duplicate pointing at another canonical (rare; prompt disallows).
 *  - `ogImage` defaults to the brand logo but should be page-specific whenever
 *    an image is available.
 *  - `schema` can be a single object or an array; each element becomes its
 *    own <script type="application/ld+json"> tag.
 *  - `noindex` is for thank-you pages and internal system pages only.
 */

export type SEOSchema = Record<string, unknown> | Array<Record<string, unknown>>;

export interface SEOHeadProps {
  title: string;
  description: string;
  canonical: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  schema?: SEOSchema;
  noindex?: boolean;
  /** Optional Twitter title override; defaults to `title`. */
  twitterTitle?: string;
  /** Optional Twitter description override; defaults to `description`. */
  twitterDescription?: string;
  /** ISO 8601 date for article-type pages. */
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  articleAuthor?: string;
}

const DEFAULT_OG_IMAGE = 'https://www.elevationelectricalfl.com/logo.png';

const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = 'website',
  schema,
  noindex = false,
  twitterTitle,
  twitterDescription,
  articlePublishedTime,
  articleModifiedTime,
  articleAuthor,
}) => {
  const schemas = schema
    ? Array.isArray(schema)
      ? schema
      : [schema]
    : [];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {!noindex && <meta name="robots" content="index, follow, max-image-preview:large" />}

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Elevation Electrical, LLC" />
      <meta property="og:locale" content="en_US" />
      {ogType === 'article' && articlePublishedTime && (
        <meta property="article:published_time" content={articlePublishedTime} />
      )}
      {ogType === 'article' && articleModifiedTime && (
        <meta property="article:modified_time" content={articleModifiedTime} />
      )}
      {ogType === 'article' && articleAuthor && (
        <meta property="article:author" content={articleAuthor} />
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={twitterTitle ?? title} />
      <meta name="twitter:description" content={twitterDescription ?? description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD */}
      {schemas.map((block, i) => (
        <script type="application/ld+json" key={i}>
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEOHead;
